import { useCallback, useEffect, useRef } from "react"
import type { MutableRefObject } from "react"
import { useSplitScreenStore } from "store/splitScreen.store"
import { openPopOutControlChannel } from "helpers/popOutWindowChannel.helper"
import type { TPopOutControlChannel } from "helpers/popOutWindowChannel.helper"
import { getEcallsForErefId } from "../quotationViewer.helpers"
import { getConnectedErefIds } from "./smartNavigation.helpers"
import type { TSmartNavSelection } from "./smartNavigation.helpers"
import type { TEcall, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"
import type { TViewerPane } from "services/annotations.service"

// Cross-window "Auto open floor plan" (#1430 review) — the pop-out's counterpart to
// `useSmartNavigation`, which drives the same idea between two PANES inside one window. Here the
// two sides are two REALMS (see usePopOutWindow's header), so there is no pane to hand a viewport
// or a tool mode to — the only thing that crosses is a bare `{ kind, id }`, posted on the same
// control channel `usePopOutWindow` already opens for this editor session, and resolved against the
// RECEIVING window's own already-synced cache (the same "no entity payload crosses" rule the
// cache-sync channel follows).
//
// Reuses `splitScreen.store`'s `isAutoOpenFloorPlan` rather than a store of its own: to the user
// it's the same toggle regardless of which second-view mechanism produced it, and the two are
// mutually exclusive (dual view is disabled while popped out, and vice versa), so one flag per
// window is enough.
//
// THIS HOOK RUNS IN BOTH WINDOWS, same as usePopOutWindow — whichever side's selection changes
// posts, and both sides listen.

type TSelectOptions = { scroll?: boolean; pane?: TViewerPane; additive?: boolean }

export type TPopOutFollowNavigationCtx = {
	// Null until an editor session exists — mirrors usePopOutWindow's own openerId, which the caller
	// passes straight through so the two hooks always agree on the channel address.
	openerId: string | null
	// A peer window is reachable right now — the opener's own `isPopOutOpen`, which the child also
	// sets true for itself on mount (see usePopOutWindow). False in every other layout (single view,
	// in-window split), where there is nothing on the other end of the channel.
	isPeerWindowOpen: boolean
	allEcallsRef: MutableRefObject<TEcall[]>
	findErefWithFurnitureById: (erefId: number) => TErefWithFurniture | undefined
	findFurnitureById: (furnitureId: number) => TFurniture | undefined
	findEcallById: (ecallId: number) => TEcall | undefined
	// The orchestrator's own click handlers — reused as-is so a peer-driven select opens the sidebar
	// and scrolls exactly like a local click would.
	handleErefClick: (eref: TErefWithFurniture, options?: TSelectOptions) => void
	handleEcallClick: (ecall: TEcall, options?: TSelectOptions) => void
}

export const usePopOutFollowNavigation = ({
	openerId,
	isPeerWindowOpen,
	allEcallsRef,
	findErefWithFurnitureById,
	findFurnitureById,
	findEcallById,
	handleErefClick,
	handleEcallClick,
}: TPopOutFollowNavigationCtx) => {
	const isFollowEnabled = useSplitScreenStore((state) => state.isAutoOpenFloorPlan)
	const setFollowEnabled = useSplitScreenStore((state) => state.setAutoOpenFloorPlan)

	const channelRef = useRef<TPopOutControlChannel | null>(null)
	// Raised while THIS window is applying a selection that arrived FROM the peer, so the click
	// handler's own notify (below) doesn't immediately post it right back — the same shape as
	// useQuotationViewer's selectInPaneSilently / suppressSmartNavRef, one realm over.
	const isApplyingPeerRef = useRef(false)

	// Bound once per channel (below); everything it needs arrives by ref so it never closes over a
	// stale render, same pattern usePopOutWindow uses for setSplitRef.
	const isFollowEnabledRef = useRef(isFollowEnabled)
	isFollowEnabledRef.current = isFollowEnabled
	const findErefRef = useRef(findErefWithFurnitureById)
	findErefRef.current = findErefWithFurnitureById
	const findFurnitureRef = useRef(findFurnitureById)
	findFurnitureRef.current = findFurnitureById
	const findEcallRef = useRef(findEcallById)
	findEcallRef.current = findEcallById
	const handleErefClickRef = useRef(handleErefClick)
	handleErefClickRef.current = handleErefClick
	const handleEcallClickRef = useRef(handleEcallClick)
	handleEcallClickRef.current = handleEcallClick

	// Turning on a peer window defaults follow mode ON (#1430 review — "should be enabled by default
	// when the user enters the pop-out mode"), mirroring #1383's own default-on-entry for the
	// in-window split. Fires once per (re)entry, not once per mount: `openerId` is minted the moment
	// the editor opens, long before any pop-out exists, so the trigger has to be the peer actually
	// appearing — otherwise every plain single-view session would silently flip the toggle on with
	// nothing yet popped out to justify it.
	const hasDefaultedRef = useRef(false)
	useEffect(() => {
		if (!isPeerWindowOpen) {
			hasDefaultedRef.current = false
			return
		}
		if (hasDefaultedRef.current) return
		hasDefaultedRef.current = true
		setFollowEnabled(true)
	}, [isPeerWindowOpen, setFollowEnabled])

	useEffect(() => {
		if (!openerId) return
		const channel = openPopOutControlChannel(openerId, (message) => {
			if (message.type !== "peer-selection") return
			if (!isFollowEnabledRef.current) return

			const { selection } = message
			let counterpart: { kind: "eref"; entity: TErefWithFurniture } | { kind: "ecall"; entity: TEcall } | null = null

			if (selection.kind === "eref") {
				const eref = findErefRef.current(selection.id)
				const ecall = eref ? getEcallsForErefId(allEcallsRef.current, eref.id)[0] : undefined
				if (ecall) counterpart = { kind: "ecall", entity: ecall }
			} else if (selection.kind === "furniture") {
				// Same rule as the in-window machine: furniture is a source only, and its counterpart is
				// its parent eref's ecalls (never the reverse).
				const furniture = findFurnitureRef.current(selection.id)
				const ecall = furniture ? getEcallsForErefId(allEcallsRef.current, furniture.eref_id)[0] : undefined
				if (ecall) counterpart = { kind: "ecall", entity: ecall }
			} else {
				const ecall = findEcallRef.current(selection.id)
				const erefId = ecall ? getConnectedErefIds(ecall)[0] : undefined
				const eref = erefId != null ? findErefRef.current(erefId) : undefined
				if (eref) counterpart = { kind: "eref", entity: eref }
			}

			// Nothing connected on the other side — leave this window showing whatever it already had,
			// same as the in-window machine leaving an empty counterpart list alone.
			if (!counterpart) return

			isApplyingPeerRef.current = true
			try {
				if (counterpart.kind === "eref") handleErefClickRef.current(counterpart.entity, { scroll: true, pane: "primary" })
				else handleEcallClickRef.current(counterpart.entity, { scroll: true, pane: "primary" })
			} finally {
				isApplyingPeerRef.current = false
			}
		})
		channelRef.current = channel
		return () => {
			channelRef.current = null
			channel.close()
		}
	}, [openerId, allEcallsRef])

	// Called from the orchestrator's notifySmartNavSelection — the one funnel every eref/ecall/
	// furniture click already goes through — so this needs no click-handler wiring of its own.
	const notifyFollowSelection = useCallback(
		(selection: TSmartNavSelection | null, pane: TViewerPane) => {
			// The pop-out never has an in-window "split" pane of its own; a selection made in one only
			// ever originates from "primary" in either realm.
			if (pane !== "primary") return
			if (!selection) return
			if (isApplyingPeerRef.current) return
			if (!isFollowEnabledRef.current) return
			if (!isPeerWindowOpen) return
			const id =
				selection.kind === "eref"
					? selection.eref.id
					: selection.kind === "ecall"
						? selection.ecall.id
						: selection.furniture.id
			channelRef.current?.post({ type: "peer-selection", selection: { kind: selection.kind, id } })
		},
		[isPeerWindowOpen],
	)

	return { notifyFollowSelection, isFollowEnabled, setFollowEnabled }
}
